import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { useEffect, useRef, useState } from "react";
import { ColumnDef, OrderModel } from "../../models/orders/order-list-model";
import Helpers from "../../utils/helpers";
import { useTranslation } from "react-i18next";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import OrderReview from "./order-review";
import myOrderServices from "../../services/orders/myorder.service";
import { Tag } from "primereact/tag";
import { getSeverity } from "../../services/order/order-list.-mock";
import { Toast, ToastMessage } from "primereact/toast";
import UserDeleteDialog from "../edit-users/user-delete-dialog";
import { TabPanel, TabView } from "primereact/tabview";
import OrderPrint from "./order-print";

interface DataTableProps {
  data: any[];
}

const OrderListDataTable: React.FC<DataTableProps> = ({ data }) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const [orderDataList, setOrderDataList] = useState<OrderModel[]>(data);
  const [visible, setVisible] = useState(false);
  const [dialogHeader, setDialogHeader] = useState(t("orderlist_orderdetails_label"));
  const [selectedOrder, setSelectedOrder] = useState<OrderModel | null>(null);
  const [localFilter, setLocalFilter] = useState<string>(" ");
  const [printEnabled, setPrintEnabled] =useState(false);
  const [selectedId, setSelectedId] = useState<number>(0);
  const [notifyHeader, setNotifyHeader] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [isNotifyVisible, setIsNotifyVisible] = useState(false);

  const handleSetOrderData = (rowData: OrderModel) => {
    setSelectedOrder(rowData);
    setVisible(true);
  };

  useEffect(() => {
    if (selectedOrder !== null) setVisible(true);
  }, [selectedOrder]);

  useEffect(() => {
    setOrderDataList(data);
  },[data]);

  const showMessage = (label: string, message: string, severity: ToastMessage['severity']) => {
    toast.current?.show({ severity: severity, summary: label, detail: message, life: 3000 });
  };

  const handleCancelOrder = (rowData: OrderModel): void => {
    setSelectedId(rowData.orderID);
    setNotifyHeader("Cancel Order");
    setNotifyMessage("Are you sure you want to cancel this order?");
    setIsNotifyVisible(true);
  }

  const cancelOrder = async () => {
    try {
      let response = await myOrderServices.updateOrderStatus({
        orderID: selectedId,
        orderStatusID: 6,
      });
      if(response.isSuccess) {
        showMessage('Cancel Order','Order cancelled successfully', 'error');
        const updatedData = updateStatusOnDelete(selectedId);
        setOrderDataList(updatedData);
      }
      else 
      {
        showMessage('Cancel Order', response.message + ': Error in cancel order', 'error');
      }
    }
    catch(error) {
      console.error(error);
    }
  };

  function updateStatusOnDelete(id: number): OrderModel[] {
    return orderDataList.map((item: OrderModel) => {
        if (item.orderID === id) {
            return { ...item, orderStatusName: "Cancelled", orderStatusID: 6 };
        }
        return item;
    });
  }

  const hideNotifyDialog = (value: boolean) => {
    if (value) {
      cancelOrder();
    }
    setIsNotifyVisible(false);
  };

  const columns: ColumnDef[] = [
    {
      field: "orderID",
      header: t("orderlist_table_orderno"),
      sortable: true,
      style: { minWidth: "7rem" },
      cell: (row: any) => (
        <span
          style={{ color: "darkcyan", fontWeight: "bolder", cursor: "pointer" }}
          onClick={() => handleSetOrderData(row)}
        >
          {row.orderID}
        </span>
      ),
    },
    {
      field: "createdDate",
      header: t("orderlist_table_orderdate"),
      style: { minWidth: "9rem" },
      sortable: true,
      cell: (row: any) =>
        new Date(row.createdDate).toLocaleDateString("en-gb", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      field: "invoiceNumber",
      header: t("orderlist_table_invoiceno"),
      style: { minWidth: "8rem" },
      //sortable: true,
      cell: (row: any) =>
        row.orderInvoice.length > 0 ? (
          <>
            <span style={{color: "darkcyan",fontWeight: "bolder" }}>
              {row.orderInvoice[0].invoiceNo}
            </span>
            <span style={{color: 'green'}}>
              {row.orderInvoice.length>1 ? " (+" + String(row.orderInvoice.length-1) + ")" : ""}
            </span>
          </>
        ) : "",
    },
    {
      field: "invoiceDate",
      header: t("orderlist_table_invoicedate"),
      style: { minWidth: "10rem" },
     // sortable: true,
      cell: (row: any) =>
        row.orderInvoice.length > 0
          ? new Date(row.orderInvoice[0].invoicedDate).toLocaleDateString("en-gb", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "",
    },
    {
      field: "orderStatusName",
      header: t("orderlist_table_orderstatus"),
      style: { minWidth: "10rem" },
      sortable: true,
      cell: (row: any) => (
        <div style={{textAlign:"center"}}         
>
          <Tag value={row.orderStatusName} severity={getSeverity(row.orderStatusID)} />
        </div>
      ),
    },
    {
      field: "grossAmount",
      header: t("orderlist_table_totalvalue"),
      style: { minWidth: "9rem" },
      sortable: true,
      cell: (row: any) =>
        Math.round(row.grossAmount).toLocaleString("en-gb", {
          style: "currency",
          currency: "INR",
        }),
    },
    {
      field: "paymentModeName",
      header: t("orderlist_table_paymenttype"),
      style: { minWidth: "10rem" },
      sortable: true,
      cell: (row: any) => Helpers.formatPaymentMode(row.paymentModeName),
    },
    {
      field: "userName",
      header: t("orderlist_table_username"),
      style: { minWidth: "10rem" },
      sortable: true,
      cell: (row: any) => row.userName,
    },
    {
      field: "primaryContactNbr",
      header: t("orderlist_table_mobile"),
      style: { minWidth: "8rem" },
      sortable: true,
      cell: (row: any) => row.primaryContactNbr,
    },
    {
      field: "userAddress",
      header: t("orderlist_table_address"),
      style: { minWidth: "20rem" },
     // sortable: true,
      cell: (row: any) =>
        row.address1 +
        " " +
        row.address2 +
        " " +
        row.landmark +
        " Pin-" +
        row.zipcode,
    },
    {
      field: "areaName",
      header: t("Area"),
      style: { minWidth: "8rem" },
      sortable: true,
      cell: (row: any) => row.areaName,
    },
    {
      field: "subDistrictName",
      header: t("SubDistrict"),
      style: { minWidth: "8rem" },
      sortable: true,
      cell: (row: any) => row.subDistrictName,
    },    
    {
      field: "districtName",
      header: t("District"),
      style: { minWidth: "8rem" },
      sortable: true,
      cell: (row: any) => row.districtName,
    },
    {
      field: "stateName",
      header: t("orderlist_table_state"),
      style: { minWidth: "8rem" },
      sortable: true,
      cell: (row: any) => row.stateName,
    },
    {
      field: "zipcode",
      header: t("PinCode"),
      style: { minWidth: "8rem" },
      sortable: true,
      cell: (row: any) => row.zipcode,
    },   
    {
      field: "gstNumber",
      header: t("orderlist_table_gstno"),
      style: { minWidth: "8rem" },
      sortable: true,
      cell: (row: any) => row.gstNumber,
    },
    {
      header: "Action",
      cell: (row: any) =>
        row.orderStatusID === 1 && row.orderInvoice.length === 0? <Button
            icon="pi pi-delete-left"
            onClick={() => handleCancelOrder(row)}
            style={{ border: 'none', color: 'tomato', background: 'none'}}
            className="p-button-rounded"
            tooltip="Cancel Order"
          /> : ""
        }
  ];

  const hideDialog = () => {
    setVisible(false);
    setDialogHeader(t("orderlist_orderdetails_label"));
  };

  const clearFilter = () => {
    setLocalFilter("");
  };

  const renderHeader = () => {
    return (
      <div className="flex justify-content-between">
        <Button
          type="button"
          icon="pi pi-filter-slash"
          label="Clear"
          outlined
          onClick={clearFilter}
        />
        <span className="p-input-icon-left border-1 border-primary">
          <i className="pi pi-filter" />
          <InputText
            type="search"
            onChange={(e) => {
              setLocalFilter(e.target.value);
            }}
            placeholder="Keyword Search"
          />
        </span>
      </div>
    );
  };

  const header = renderHeader();

  const footerContent = (
    <div>
      <Button label="Close" icon="pi pi-times" onClick={() => setVisible(false)} className="p-button-text" />
      <Button label="Print" icon="pi pi-print" onClick={() => setPrintEnabled(true)} autoFocus />
    </div>
  );

  const handleTriggerPrint = (val: boolean) => {
    setPrintEnabled(val);
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div className="col-12 card border-noround p-2">
        {orderDataList !== null ?<DataTable
            value={orderDataList}
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            stripedRows
            showGridlines
            globalFilter={localFilter || " "}
            header={header}
            emptyMessage="No order found."
            sortMode="multiple"
            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
          >
            {columns.map((col, i) => (
              <Column
                key={i}
                field={col.field}
                sortable={col.sortable}
                header={col.header}
                style={col.style}
                body={col.cell}
              />
            ))}
          </DataTable> : ""}
      </div>
      <UserDeleteDialog
        messageHeader={notifyHeader}
        notifyMessage={notifyMessage}
        isVisible={isNotifyVisible}
        isHide={hideNotifyDialog}
      />
      {selectedOrder !== undefined || selectedOrder !== null ? (
        <Dialog 
        header={dialogHeader} 
        visible={visible} 
        style={{ width: "67vw" }} 
        onHide={hideDialog}
        // footer={footerContent}
        // className="orderlist-dialog"
        >
          <TabView>
            <TabPanel header="Order Details" leftIcon="pi pi-file mr-1">
              <OrderReview TriggerPrint={handleTriggerPrint} PrintEnabled={printEnabled} OrderData={selectedOrder} />
            </TabPanel>
            <TabPanel header="Order Print" leftIcon="pi pi-print mr-1">
              <OrderPrint orderinfo={selectedOrder} />
            </TabPanel>
          </TabView>
        </Dialog>
      ) : null}
    </>
  );
};
export default OrderListDataTable;